import { useState, useRef, useEffect } from 'react';
import { FiSun, FiZap, FiWind, FiCircle, FiGrid, FiLayers, FiPackage, FiChevronDown } from 'react-icons/fi';

/** Icon component per session asset type (for mobile dropdown button). */
const ASSET_TYPE_ICONS = {
  combined_exit_emergency: FiSun,
  combined_db_rcd: FiZap,
  combined_fan_heater: FiWind,
  duress_button: FiCircle,
  automated_gate: FiGrid,
  external_lighting_control: FiLayers,
  workshop_equipment: FiPackage,
};
function getAssetTypeIcon(assetType) {
  return ASSET_TYPE_ICONS[assetType] || FiZap;
}

/**
 * The single-row session toolbar: desktop asset-type select, mobile asset-type
 * dropdown, equipment-code search, and the work order badge. Owns the mobile
 * dropdown open state and its click-outside dismissal. Extracted verbatim from
 * TestSessionView.
 */
export function SessionToolbar({
  sessionAssetType,
  assetOptionsForElement,
  onAssetTypeChange,
  equipmentCodeSearch,
  onEquipmentSearchChange,
  isMobile,
  workOrderNumber,
}) {
  const [mobileAssetTypeDropdownOpen, setMobileAssetTypeDropdownOpen] = useState(false);
  const mobileAssetTypeDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileAssetTypeDropdownRef.current && !mobileAssetTypeDropdownRef.current.contains(e.target)) setMobileAssetTypeDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="session-toolbar">
      <div className="session-toolbar-row session-toolbar-row-single">
        {/* Desktop: asset type select */}
        <div className="session-toolbar-asset-type-desktop">
          <label htmlFor="sticky-asset-type" className="sr-only">Asset Type</label>
          <select
            id="sticky-asset-type"
            value={sessionAssetType}
            onChange={onAssetTypeChange}
            className="session-sticky-asset-type-select"
          >
            <option value="">Select Asset Type...</option>
            {assetOptionsForElement.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        {/* Mobile: asset type dropdown (icon only) */}
        <div className="session-toolbar-mobile-type-wrap" ref={mobileAssetTypeDropdownRef}>
          <div className="session-toolbar-mobile-type">
              <button
                type="button"
                className="session-toolbar-mobile-type-btn"
                onClick={() => setMobileAssetTypeDropdownOpen((o) => !o)}
                aria-expanded={mobileAssetTypeDropdownOpen}
                aria-haspopup="listbox"
                aria-label="Select asset type"
                title={assetOptionsForElement.find((o) => o.value === sessionAssetType)?.label || 'Asset type'}
              >
                {(() => {
                  const Icon = getAssetTypeIcon(sessionAssetType);
                  return <Icon aria-hidden className="session-toolbar-mobile-type-icon" />;
                })()}
                <FiChevronDown aria-hidden className="session-toolbar-mobile-type-chevron" />
              </button>
              {mobileAssetTypeDropdownOpen && (
                <ul className="session-toolbar-mobile-type-dropdown" role="listbox">
                  {assetOptionsForElement.map((t) => (
                    <li key={t.value} role="option" aria-selected={sessionAssetType === t.value}>
                      <button
                        type="button"
                        className="session-toolbar-mobile-type-option"
                        onClick={() => {
                          onAssetTypeChange({ target: { value: t.value } });
                          setMobileAssetTypeDropdownOpen(false);
                        }}
                      >
                        {(() => {
                          const Icon = getAssetTypeIcon(t.value);
                          return <Icon aria-hidden />;
                        })()}
                        <span>{t.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
        </div>
        {/* Search: beside asset type on same row */}
        <div className="session-toolbar-search">
          <input
            id="sticky-equipment-search"
            type="search"
            value={equipmentCodeSearch}
            onChange={onEquipmentSearchChange}
            placeholder="Search equipment code"
            className="session-sticky-search-input"
            aria-label="Search equipment code"
          />
        </div>
        {workOrderNumber && !isMobile && (
          <div className="session-sticky-meta session-sticky-wo">
            WO: {workOrderNumber}
          </div>
        )}
      </div>
    </div>
  );
}
